/**
 * ExpertBadge Component
 *
 * Displays expert persona badge with model-specific styling.
 */

import { clsx } from 'clsx';
import type { LLMProvider } from '../../../lib/api';

interface ExpertBadgeProps {
  model: LLMProvider;
  persona?: string | null;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const EXPERT_CONFIG: Record<
  LLMProvider,
  { name: string; icon: string; color: string; bgColor: string; borderColor: string }
> = {
  CLAUDE_OPUS_4_5: {
    name: 'Dr. Precision',
    icon: '🩺',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
  CLAUDE_OPUS_4_6: {
    name: 'Dr. Precision 4.6',
    icon: '🩺',
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
  },
  GPT_5_2_PRO: {
    name: 'Dr. Evidence',
    icon: '📊',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
  GEMINI_3_PRO: {
    name: 'Dr. Advocate',
    icon: '🤝',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
};

const SIZE_CLASSES = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export function ExpertBadge({ model, persona, showIcon = true, size = 'md' }: ExpertBadgeProps) {
  const config = EXPERT_CONFIG[model] || {
    name: model,
    icon: '💬',
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  };

  const displayName = persona || config.name;

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        config.bgColor,
        config.color,
        config.borderColor,
        SIZE_CLASSES[size]
      )}
    >
      {showIcon && <span>{config.icon}</span>}
      {displayName}
    </span>
  );
}

export function ModeratorBadge({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        'bg-indigo-50 text-indigo-700 border-indigo-200',
        SIZE_CLASSES[size]
      )}
    >
      <span>👨‍⚕️</span>
      Chief of Medicine
    </span>
  );
}

export function getExpertColor(model: LLMProvider): string {
  return EXPERT_CONFIG[model]?.color || 'text-gray-700';
}

export function getExpertBgColor(model: LLMProvider): string {
  return EXPERT_CONFIG[model]?.bgColor || 'bg-gray-50';
}
